/**
 * Session-expiry coordination.
 *
 * Kept free of React so the decisions that matter — "is this an expiry", "has it
 * already been reported", "has the session come back" — can be unit tested in Node
 * without a DOM.
 *
 * ── Why expiry is a separate state rather than "signed out" ──────────────────
 *
 * Flipping the app to signed-out the instant a 401 arrives makes RequireAuth unmount
 * the page the operator is on. A half-typed campaign vanishes and the operator lands
 * on the login screen with no idea why. Instead an expiry is recorded here, the
 * current screen is left exactly as it is, and a blocking dialog explains what
 * happened. The app only signs out when the operator chooses "Log In Again".
 */

/**
 * One store per AuthProvider.
 *
 * `report()` is idempotent by construction: however many requests fail together,
 * only the first one flips the state, so only one dialog can ever open.
 */
export function createSessionExpiryStore() {
  let expired = false;
  // True while a deliberate sign-out is in flight. A /check-auth or 401 racing a
  // logout is the logout's own doing, not an expiry, and must not open the dialog.
  let suppressed = false;
  const listeners = new Set();

  const emit = () => {
    for (const listener of listeners) listener();
  };

  return {
    getSnapshot: () => expired,

    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },

    /**
     * Records that the server rejected the session.
     *
     * Ignored when nobody was signed in: a 401 on the login screen, or during the
     * first /check-auth of a cold load, is just "not signed in", and RequireAuth
     * already handles that by redirecting. Opening a dialog there would be the
     * login-page loop this guards against.
     *
     * @param {{authenticated: boolean}} state Whether the app currently believes it is signed in.
     * @returns {boolean} True only for the report that changed the state.
     */
    report({ authenticated }) {
      if (expired || suppressed || !authenticated) return false;
      expired = true;
      emit();
      return true;
    },

    clear() {
      if (!expired) return;
      expired = false;
      emit();
    },

    setSuppressed(value) {
      suppressed = Boolean(value);
    },

    isSuppressed: () => suppressed
  };
}

/**
 * Interprets a /check-auth answer against what the app last knew.
 *
 * /check-auth never answers 401 — it reports `{authenticated:false}` with a 200 — so
 * a session that dies while the tab is in the background shows up here, on the
 * focus refetch, rather than through the 401 path. Treated the same way: if the app
 * believed it was signed in, that is an expiry, and the previous session data is
 * kept so the screen does not unmount underneath the dialog.
 *
 * @param {object} input
 * @param {{authenticated?: boolean, user?: {email?: string}}|undefined} input.previous
 * @param {{authenticated?: boolean, user?: {email?: string}}} input.result
 * @param {boolean} input.expired    Whether an expiry is already showing.
 * @param {boolean} input.suppressed Whether a deliberate sign-out is in flight.
 * @returns {{data: object, action: 'none'|'expire'|'recover'}}
 */
export function resolveSessionCheck({ previous, result, expired, suppressed }) {
  const wasAuthenticated = Boolean(previous?.authenticated);

  if (result?.authenticated) {
    if (!expired) return { data: result, action: 'none' };

    // Signed back in elsewhere (another tab shares the cookie). Only the same
    // operator may silently pick the session back up: anyone else would inherit a
    // screen full of the previous operator's draft and SMTP settings.
    const previousEmail = previous?.user?.email;
    const sameOperator = Boolean(previousEmail) && previousEmail === result.user?.email;
    return sameOperator ? { data: result, action: 'recover' } : { data: previous, action: 'none' };
  }

  if (wasAuthenticated && !suppressed) return { data: previous, action: 'expire' };

  return { data: result ?? { authenticated: false }, action: 'none' };
}
